'use client';

import { useEffect, useRef } from 'react';

/* ───────────────────────────────────────────
   AmbientBackground
   Orbes flutuantes + grid animado + feixes de luz
   Reutilizável: landing, login, 404, etc.
   ─────────────────────────────────────────── */

interface AmbientBackgroundProps {
  /** Orbes mais suaves (menos opacidade) */
  subtle?: boolean;
  /** Remove o grid */
  noGrid?: boolean;
  /** Cor primária dos elementos (formato rgb(a)) */
  primaryColor?: string;
  /** Cor secundária */
  secondaryColor?: string;
}

export function AmbientBackground({
  subtle = false,
  noGrid = false,
  primaryColor = '6, 147, 229',
  secondaryColor = '0, 217, 255',
}: AmbientBackgroundProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId: number;
    let mouseX = 0;
    let mouseY = 0;

    const resize = (): void => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Orbes ──
    const orbs = Array.from({ length: 4 }, (_, i) => ({
      x: canvas.width * (0.15 + i * 0.23),
      y: canvas.height * (0.2 + (i % 2) * 0.5),
      radius: 180 + i * 40 + Math.random() * 60,
      dx: (0.15 + Math.random() * 0.15) * (i % 2 === 0 ? 1 : -1),
      dy: (0.1 + Math.random() * 0.1) * (i % 2 === 0 ? -1 : 1),
      color: i % 2 === 0 ? primaryColor : secondaryColor,
    }));

    const draw = (): void => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ── Grid drift ──
      if (!noGrid) {
        const gridSize = 48;
        const offset = (Date.now() * 0.02) % gridSize;
        ctx.strokeStyle = `rgba(${primaryColor}, ${subtle ? 0.03 : 0.06})`;
        ctx.lineWidth = 1;

        for (let x = offset; x < canvas.width; x += gridSize) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvas.height);
          ctx.stroke();
        }
        for (let y = offset; y < canvas.height; y += gridSize) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(canvas.width, y);
          ctx.stroke();
        }
      }

      // ── Orbes ──
      const baseOpacity = subtle ? 0.12 : 0.2;
      for (const orb of orbs) {
        const gradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
        gradient.addColorStop(0, `rgba(${orb.color}, ${baseOpacity + 0.08})`);
        gradient.addColorStop(0.4, `rgba(${orb.color}, ${baseOpacity * 0.5})`);
        gradient.addColorStop(1, `rgba(${orb.color}, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();

        // Movement
        orb.x += orb.dx;
        orb.y += orb.dy;

        if (orb.x < -orb.radius || orb.x > canvas.width + orb.radius) orb.dx *= -1;
        if (orb.y < -orb.radius || orb.y > canvas.height + orb.radius) orb.dy *= -1;

        // Mouse attraction (subtle)
        orb.dx += (mouseX - orb.x) * 0.00002;
        orb.dy += (mouseY - orb.y) * 0.00002;

        // Damping
        orb.dx *= 0.999;
        orb.dy *= 0.999;

        // Speed cap
        const speed = Math.sqrt(orb.dx * orb.dx + orb.dy * orb.dy);
        const maxSpeed = 1.2;
        if (speed > maxSpeed) {
          orb.dx = (orb.dx / speed) * maxSpeed;
          orb.dy = (orb.dy / speed) * maxSpeed;
        }
      }

      // ── Beams (linhas verticais suaves) ──
      const beamPositions = [canvas.width * 0.18, canvas.width * 0.5, canvas.width * 0.82];
      const beamHeight = canvas.height;

      for (const bx of beamPositions) {
        const beamGrad = ctx.createLinearGradient(bx, 0, bx, beamHeight);
        const beamOpacity = subtle ? 0.02 : 0.035;
        beamGrad.addColorStop(0, `rgba(${primaryColor}, 0)`);
        beamGrad.addColorStop(0.3, `rgba(${primaryColor}, ${beamOpacity})`);
        beamGrad.addColorStop(0.5, `rgba(${secondaryColor}, ${beamOpacity * 1.3})`);
        beamGrad.addColorStop(0.7, `rgba(${primaryColor}, ${beamOpacity})`);
        beamGrad.addColorStop(1, `rgba(${primaryColor}, 0)`);

        ctx.fillStyle = beamGrad;
        ctx.fillRect(bx - 1, 0, 2, beamHeight);
      }

      rafId = requestAnimationFrame(draw);
    };

    const handleMouse = (e: MouseEvent): void => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener('mousemove', handleMouse);

    draw();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouse);
    };
  }, [subtle, noGrid, primaryColor, secondaryColor]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
